/**
 * Join/create/re-key flow — the room-entry orchestration extracted from
 * main.ts (META-06 cut 2). Everything here is decision logic that belongs
 * under coverage: which surface renders on which typed failure, the
 * decision-14 re-key retry loop, the E2E-13b seat-removed front door, the
 * one-time GM-key reveal gate, and the banner-suppression lifecycle.
 *
 * Dependencies are injected so the flow is unit-testable without a
 * websocket or a real fetch; main.ts supplies the real ones and stays pure
 * wiring (§7.2's premise for its exemption).
 */

import { JOIN_REJECTION, type Command } from "@asarimitsuha/table-core";
import type {
  ConnectOptions,
  SeatAssignment,
  TableConnection,
} from "./net/connection.js";
import {
  NAME_STORAGE_KEY,
  buildGmJoinPath,
  buildJoinPath,
  seatSecretStorageKey,
  seatStorageKey,
} from "./net/joinParams.js";
import { renderGmKeyReveal } from "./panels/gmKeyReveal.js";
import { renderError, renderJoinForm } from "./panels/lobby.js";
import { rejectionMessage } from "./panels/rejectionCopy.js";
import { renderRejectionNotice } from "./panels/rejectionNotice.js";
import { renderSeatRemovedNotice } from "./panels/removeSeat.js";

export interface JoinFlowDeps {
  fetchFn: typeof fetch;
  storage: Pick<Storage, "getItem" | "setItem">;
  connect(
    options: ConnectOptions,
    onSeatAssigned: (assignment: SeatAssignment) => void,
  ): Promise<TableConnection>;
  endpoint(): string;
  origin(): string;
  /** history.replaceState wrapper — puts the GM link in the URL bar. */
  replaceUrl(path: string): void;
  /** The lobby/table panel mount and the rejection-notice mount. */
  panel: HTMLElement;
  notices: HTMLElement;
  /** Called with a live connection; main.ts mounts the table (canvas+panels). */
  onConnected(
    connection: TableConnection,
    code: string,
    gmKey: string | null,
  ): Promise<void>;
}

export interface JoinFlow {
  enterTable(code: string, gmKey: string | null): Promise<void>;
  createTable(tableName: string): Promise<void>;
  /** The decision-14 re-key submit: leave, rejoin with the key, retry
   *  keyless with an inline error if it was wrong. */
  submitGmKey(table: TableConnection, code: string, key: string): void;
  uploadMapImage(
    code: string,
    gmKey: string | null,
    file: File,
    send: (command: Command) => void,
  ): Promise<void>;
  /** Wire a live table's rejection + removal surfaces. */
  attachTableEvents(table: TableConnection): void;
  /** Inline error for the re-key form; cleared on each new attempt. */
  getRekeyError(): string | null;
  /** True while another surface owns the disconnect story. */
  isBannerSuppressed(): boolean;
  suppressBanner(): void;
}

export function createJoinFlow(deps: JoinFlowDeps): JoinFlow {
  let rekeyError: string | null = null;
  let bannerSuppressed = false;

  async function joinTable(
    code: string,
    gmKey: string | null,
    playerName: string,
    rekeyAttempt = false,
  ): Promise<void> {
    deps.storage.setItem(NAME_STORAGE_KEY, playerName);
    let connection: TableConnection;
    try {
      connection = await deps.connect(
        {
          endpoint: deps.endpoint(),
          code,
          name: playerName,
          seatId: deps.storage.getItem(seatStorageKey(code)) ?? undefined,
          seatSecret: deps.storage.getItem(seatSecretStorageKey(code)) ?? undefined,
          gmKey: gmKey ?? undefined,
        },
        ({ seatId, seatSecret }) => {
          deps.storage.setItem(seatStorageKey(code), seatId);
          deps.storage.setItem(seatSecretStorageKey(code), seatSecret);
        },
      );
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      if (rekeyAttempt) {
        // Wrong key on an in-room re-key: return the GM to the table on
        // their stored player seat with an inline error on the re-key form —
        // never strand them on an error screen (decision 14).
        rekeyError = rejectionMessage(reason);
        await joinTable(code, null, playerName);
        return;
      }
      // The raw typed code rides data-reason on the rejection notice — the
      // one surface tests bind to; the in-panel error stays generic (§6.2).
      renderRejectionNotice(deps.notices, reason);
      if (reason === JOIN_REJECTION.seatRemoved) {
        // §5.2 layer 3 through the front door (E2E-13b): the same specific
        // told-why notice as a live removal — never a generic error over a
        // silent fresh reseat.
        renderSeatRemovedNotice(deps.panel);
        return;
      }
      renderError(deps.panel, rejectionMessage(reason));
      return;
    }
    bannerSuppressed = false; // a live connection: the banner may speak again
    await deps.onConnected(connection, code, gmKey);
  }

  async function enterTable(code: string, gmKey: string | null): Promise<void> {
    const storedName = deps.storage.getItem(NAME_STORAGE_KEY) ?? "";
    const roomRes = await deps.fetchFn(`/api/rooms/${code}`);
    if (!roomRes.ok) {
      renderError(deps.panel, `No table found for code ${code}.`);
      return;
    }
    const room = (await roomRes.json()) as { name: string };
    renderJoinForm(deps.panel, room.name, storedName, {
      onJoin: (playerName) => {
        void joinTable(code, gmKey, playerName);
      },
    });
  }

  return {
    enterTable,

    async createTable(tableName: string): Promise<void> {
      const res = await deps.fetchFn("/api/rooms", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: tableName }),
      });
      if (!res.ok) {
        renderError(deps.panel, "Could not create the table. Try again.");
        return;
      }
      const created = (await res.json()) as { code: string; gmKey: string };
      // The GM link is the way back in after a reload; into the URL bar
      // immediately so even a panicked bookmark captures it.
      deps.replaceUrl(buildGmJoinPath(created.code, created.gmKey));
      // One-time key reveal (M3.5): entry is gated behind an explicit
      // "I've saved it" confirmation — the key is never shown again.
      renderGmKeyReveal(
        deps.panel,
        {
          code: created.code,
          gmKey: created.gmKey,
          joinUrl: deps.origin() + buildJoinPath(created.code),
          gmUrl: deps.origin() + buildGmJoinPath(created.code, created.gmKey),
        },
        {
          onContinue: () => {
            void enterTable(created.code, created.gmKey);
          },
        },
      );
    },

    submitGmKey(table: TableConnection, code: string, key: string): void {
      rekeyError = null;
      bannerSuppressed = true; // deliberate leave+rejoin, not a drop
      void table
        .leave()
        .then(() =>
          joinTable(code, key, deps.storage.getItem(NAME_STORAGE_KEY) ?? "GM", true),
        );
    },

    async uploadMapImage(code, gmKey, file, send): Promise<void> {
      if (!gmKey) {
        renderRejectionNotice(deps.notices, "gm_only");
        return;
      }
      const res = await deps.fetchFn(`/api/rooms/${code}/map`, {
        method: "POST",
        headers: { "content-type": file.type, "x-gm-key": gmKey },
        body: file,
      });
      if (!res.ok) {
        renderRejectionNotice(deps.notices, "malformed_command");
        return;
      }
      const { imageUrl } = (await res.json()) as { imageUrl: string };
      send({ type: "setMap", imageUrl });
    },

    attachTableEvents(table: TableConnection): void {
      table.onRejection((rejection) =>
        renderRejectionNotice(deps.notices, rejection.reason),
      );
      table.onSeatRemoved((info) => {
        // §5.2 layer 3: name who — server message first, roster fallback.
        // The disconnect banner stands down: "reload to rejoin" would lie.
        bannerSuppressed = true;
        const gm = Object.values(table.getState().seats).find((s) => s.role === "gm");
        renderSeatRemovedNotice(deps.panel, {
          removedByName: info.removedByName ?? gm?.name ?? null,
        });
      });
    },

    getRekeyError: () => rekeyError,
    isBannerSuppressed: () => bannerSuppressed,
    suppressBanner: () => {
      bannerSuppressed = true;
    },
  };
}
